import React, { useEffect, useState } from "react";

import { Chip, CircularProgress } from "@mui/material";
import AirplanemodeActiveRounded from "@mui/icons-material/AirplanemodeActiveRounded";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import PeopleAltOutlined from "@mui/icons-material/PeopleAltOutlined";

import { useSetRecoilState } from "recoil";
import { snackbarData } from "recoilState/GlobalState";

import { migraUsuarioPasoGet, migraVueloGet, migraVueloUpd } from "services/services-mia/migra";
import { handleResponse, transformDateTime } from "services/Local-services";
import { FormButton } from "components";
import { ESTADO_LABELS, mapMigraUsuarioPasoRow } from "pages/migra/utils/usuarioPaso";

import styles from "./VueloDetalleModal.module.scss";

const TIPO_CHIP = {
    E: { label: "Entrada", color: "#166534", background: "#f0fdf4", border: "#86efac" },
    S: { label: "Salida", color: "#991b1b", background: "#fef2f2", border: "#fca5a5" },
};

const VUELO_ESTADO_CHIP = {
    A: { label: "Abierto", color: "#166534", background: "#f0fdf4", border: "#86efac" },
    C: { label: "Cerrado", color: "#991b1b", background: "#fef2f2", border: "#fca5a5" },
};

const PASAJEROS_ESTADO_CHIP = {
    P: { color: "#374151", background: "#f3f4f6", border: "#d1d5db" },
    V: { color: "#991b1b", background: "#fef2f2", border: "#fca5a5" },
    C: { color: "#166534", background: "#f0fdf4", border: "#86efac" },
    A: { color: "#92400e", background: "#fffbeb", border: "#fcd34d" },
    B: { color: "#374151", background: "#f3f4f6", border: "#d1d5db" },
};

function EstadoVueloChip({ estado }) {
    const chip = VUELO_ESTADO_CHIP[estado] ?? { label: estado, color: "#374151", background: "#f3f4f6", border: "#d1d5db" };
    return (
        <Chip
            label={chip.label}
            size="small"
            sx={{ color: chip.color, backgroundColor: chip.background, border: `1px solid ${chip.border}`, fontWeight: 600 }}
        />
    );
}

function TipoChip({ tipo }) {
    if (!tipo) return <span className={styles.value}>-</span>;
    const chip = TIPO_CHIP[tipo] ?? { label: tipo, color: "#374151", background: "#f3f4f6", border: "#d1d5db" };
    return (
        <Chip
            label={chip.label}
            size="small"
            sx={{ color: chip.color, backgroundColor: chip.background, border: `1px solid ${chip.border}`, fontWeight: 600 }}
        />
    );
}

function PasajeroEstadoChip({ estado }) {
    const palette = PASAJEROS_ESTADO_CHIP[estado] ?? PASAJEROS_ESTADO_CHIP.B;
    return (
        <Chip
            label={ESTADO_LABELS[estado] ?? estado}
            size="small"
            sx={{ color: palette.color, backgroundColor: palette.background, border: `1px solid ${palette.border}`, fontWeight: 600 }}
        />
    );
}

function DataRows({ rows }) {
    return (
        <table className={styles.data}>
            <tbody>
                {rows.map(([label, value]) => (
                    <tr key={label}>
                        <td className={styles.label}>{label}</td>
                        <td>{value ?? <span className={styles.value}>-</span>}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

export default function VueloDetalleModal({
    row,
    updateList,
    closeModal,
    credentials,
    setRowSelected,
    mostrarPasajeros = false,
}) {
    const [loading, setLoading] = useState(true);
    const [inRequest, setInRequest] = useState(false);
    const [vuelo, setVuelo] = useState(null);
    const [pasajeros, setPasajeros] = useState([]);
    const [loadingPasajeros, setLoadingPasajeros] = useState(mostrarPasajeros);
    const setSnackbarInfo = useSetRecoilState(snackbarData);

    useEffect(() => {
        (async () => {
            const response = await migraVueloGet({ migraVueloId: row.migraVueloId }, credentials);

            if (response?.status?.code === 1) {
                setVuelo((response.result || [])[0] || null);
            } else {
                setSnackbarInfo({
                    message: response?.status?.errmsg || "No se pudo obtener el detalle del vuelo",
                    severity: "error",
                    open: true,
                });
            }
            setLoading(false);
        })();
    }, [row.migraVueloId, credentials, setSnackbarInfo]);

    useEffect(() => {
        if (!mostrarPasajeros) return;

        (async () => {
            const response = await migraUsuarioPasoGet({ migraVueloId: row.migraVueloId }, credentials);

            if (response?.status?.code === 1) {
                setPasajeros((response.result || []).map(mapMigraUsuarioPasoRow));
            } else {
                setSnackbarInfo({
                    message: response?.status?.errmsg || "No se pudieron obtener los pasajeros",
                    severity: "error",
                    open: true,
                });
            }
            setLoadingPasajeros(false);
        })();
    }, [mostrarPasajeros, row.migraVueloId, credentials, setSnackbarInfo]);

    const handleCambiarEstado = async () => {
        setInRequest(true);

        const migraVueloEstado = vuelo.migraVueloEstado === "A" ? "C" : "A";
        const response = await migraVueloUpd(
            { migraVueloId: vuelo.migraVueloId, migraVueloEstado },
            credentials
        );
        handleResponse(
            response,
            migraVueloEstado === "C" ? "Vuelo cerrado exitosamente" : "Vuelo abierto exitosamente",
            {
                setSnackbarInfo,
                setInRequest,
                setIsOpen: closeModal,
                setSelected: setRowSelected,
                updateList,
            }
        );
    };

    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
                <CircularProgress />
            </div>
        );
    }

    if (!vuelo) {
        return (
            <div style={{ textAlign: "center", padding: "2rem", color: "#4b5563" }}>
                No se encontró información del vuelo.
            </div>
        );
    }

    const pasajerosPorEstado = Object.entries(vuelo.pasajerosPorEstado || {});

    return (
        <div className={styles.container}>
            <div className={styles.sectionsRow}>
                <section className={styles.section}>
                    <h4 className={styles.sectionTitle}>
                        <AirplanemodeActiveRounded /> Datos del vuelo
                    </h4>
                    <DataRows rows={[
                        ["Paso", vuelo.migraPasoNombre],
                        ["Tipo", <TipoChip tipo={vuelo.migraVueloTipo} />],
                        ["Fecha", vuelo.migraVueloFecha ? transformDateTime(vuelo.migraVueloFecha) : null],
                        ["Identificación", vuelo.migraVueloIdentificacion],
                        [vuelo.migraVueloTipo === "S" ? "Destino" : "Origen", vuelo.migraVueloOrigenDestino],
                        ["Estado", <EstadoVueloChip estado={vuelo.migraVueloEstado} />],
                    ]} />
                </section>

                <section className={styles.section}>
                    <h4 className={styles.sectionTitle}>
                        <GroupsOutlined /> Pasajeros por estado
                    </h4>
                    <div className={styles.passengersWrap}>
                        <table className={styles.passengersTable}>
                            <thead>
                                <tr>
                                    <th>Estado</th>
                                    <th>Cantidad</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pasajerosPorEstado.length === 0 ? (
                                    <tr>
                                        <td colSpan={2}>Sin pasajeros asignados</td>
                                    </tr>
                                ) : (
                                    pasajerosPorEstado.map(([estado, cantidad]) => (
                                        <tr key={estado}>
                                            <td><PasajeroEstadoChip estado={estado} /></td>
                                            <td>{cantidad}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            {mostrarPasajeros && (
                <section className={styles.section}>
                    <h4 className={styles.sectionTitle}>
                        <PeopleAltOutlined /> Pasajeros
                    </h4>
                    {loadingPasajeros ? (
                        <div style={{ display: "flex", justifyContent: "center", padding: "1rem" }}>
                            <CircularProgress size={24} />
                        </div>
                    ) : pasajeros.length === 0 ? (
                        <span className={styles.value}>Sin pasajeros asignados</span>
                    ) : (
                        <div className={styles.passengersWrap}>
                            <table className={styles.passengersTable}>
                                <thead>
                                    <tr>
                                        <th>Apellido y Nombre</th>
                                        <th>Documento</th>
                                        <th>Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pasajeros.map((pasajero) => (
                                        <tr key={pasajero.migraUsuarioPasoId}>
                                            <td>{pasajero.apellidoYNombre}</td>
                                            <td>{pasajero.usuarioNroDeDocumentoX}</td>
                                            <td><PasajeroEstadoChip estado={pasajero.migraUsuarioPasoEstado} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            )}

            <div className={styles.footer}>
                <FormButton
                    inRequest={inRequest}
                    newFormData={{ button: vuelo.migraVueloEstado === "A" ? "Cerrar Vuelo" : "Abrir Vuelo" }}
                    onCloseModal={closeModal}
                    onClick={handleCambiarEstado}
                />
            </div>
        </div>
    );
}
